import { Object3D, Raycaster, Scene, Vector2, type Intersection, type Object3DEventMap, type WebGLRenderer } from 'three'
import { cameraControls, focusOnObject } from '../../stores/camera'
import { get } from 'svelte/store'
import useCanvasSelection from '../../hooks/canvas-selection'
import { ThreeItemKeys, ThreeItemTypes } from '../../constants/three'
import { getPrinterId } from '../../helpers/printer'

const getElementsFromIntersection = (renderer: WebGLRenderer, e: MouseEvent, scene: Scene) => {
  if (!renderer.domElement.parentElement?.contains(e.target as Node)) {
    return null
  }

  const cameraControlsValue = get(cameraControls)
  const raycaster = new Raycaster()
  const canvasPosition = renderer.domElement.getBoundingClientRect()
  const mouseX = e.clientX - canvasPosition.left
  const mouseY = e.clientY - canvasPosition.top
  const pointer = new Vector2(
    2 * (mouseX / renderer.domElement.clientWidth) - 1,
    -(2 * (mouseY / renderer.domElement.clientHeight) - 1),
  )

  if (cameraControlsValue?.camera && scene) {
    raycaster.setFromCamera(pointer, cameraControlsValue?.camera)
    return raycaster.intersectObjects(scene.children, true)
  }

  return []
}

// Solo-group substitution (a file alone in its group must never end up as the
// actual selection) is enforced centrally in useCanvasSelection's set()/add(),
// not here - so it can't be bypassed depending on which click path resolved
// this raycast. This always returns the literally clicked/tagged item.
const findElementFromIntersect = (intersects: Intersection<Object3D<Object3DEventMap>>[]) => {
  const intersectableItems = [
    ThreeItemTypes.FILE,
    ThreeItemTypes.GROUP,
    ThreeItemTypes.PRINTER,
    ThreeItemTypes.FORBIDDEN_ZONE,
  ]

  for (let i = 0; i < intersects.length; i++) {
    const intersect = intersects[i]

    if (
      intersect.object.visible
      && intersectableItems.includes(
        intersect.object.userData[ThreeItemKeys.ITEM_TYPE],
      )
    ) {
      return intersect.object
    }

    let foundObject: Object3D | undefined
    intersect.object.traverseAncestors((object) => {
      if (
        object.visible
        && intersectableItems.includes(
          object.userData[ThreeItemKeys.ITEM_TYPE],
        )
      ) {
        if (!foundObject) {
          foundObject = object
        }
      }
    })

    if (foundObject) {
      return foundObject
    }
  }
}

export const selectClickedElement = (renderer: WebGLRenderer, e: MouseEvent, scene: Scene) => {
  const { upsert, clear } = useCanvasSelection()
  const intersects = getElementsFromIntersection(renderer, e, scene)

  if (intersects === null) {
    return
  }

  if (intersects.length === 0) {
    // Be careful where clear is called.
    // We call it here to make sure that nothing has been clicked on, because the rest
    // of the algorithm excludes clicks on TransformControls
    return clear()
  }

  const foundItem = findElementFromIntersect(intersects)

  if (foundItem) {
    upsert(foundItem, e && (e.ctrlKey || e.metaKey))
  }
  else {
    clear()
  }
}

export const focusClickedElement = (renderer: WebGLRenderer, e: MouseEvent, scene: Scene) => {
  const intersects = getElementsFromIntersection(renderer, e, scene)
  if (!intersects?.length) return
  const found = findElementFromIntersect(intersects)
  if (found) focusOnObject(found)
}

export const assignPrinterIfSelected = (renderer: WebGLRenderer, e: MouseEvent, scene: Scene, onAssign: (fileId: string, printerId: string) => void) => {
  const { hasSinglePrinter, object } = useCanvasSelection()

  if (!hasSinglePrinter()) {
    return
  }

  const intersects = getElementsFromIntersection(renderer, e, scene)

  if (intersects === null) {
    return
  }

  const foundItem = findElementFromIntersect(intersects)

  if (foundItem && foundItem.userData[ThreeItemKeys.ITEM_TYPE] !== ThreeItemTypes.PRINTER) {
    onAssign(foundItem.uuid, getPrinterId(get(object) as Object3D))
  }
}
